import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { AdminLayout } from "../components/admin/AdminSidebar";
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingBag, Users, Eye, Heart,
  Package, AlertTriangle, Clock, ArrowUpRight, ArrowDownRight, BarChart3, Loader2, UserCheck, UserPlus, Receipt
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer
} from "recharts";
import DrilldownPanel from "../components/admin/DrilldownPanel";
import EmailComposer from "../components/admin/EmailComposer";

const API = process.env.REACT_APP_BACKEND_URL;
const COLORS = ["#9A7B4F", "#6B5B4F", "#D4B8B4", "#F0D9D5", "#4A7C59", "#8B6B3F", "#B8956A", "#7A9E7E"];

function getToken() {
  return localStorage.getItem("adminToken");
}

async function fetchApi(path, params = "") {
  const res = await fetch(`${API}/api/admin/analytics${path}${params}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error("fetch failed");
  return res.json();
}

async function fetchProfit(path, params = "") {
  const res = await fetch(`${API}/api/admin/profitability${path}${params}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error("fetch failed");
  return res.json();
}

// ─── KPI Card ────────────────────────────
function KpiCard({ label, value, prev, prefix = "", suffix = "", icon: Icon, subtitle, testId, onClick }) {
  const change = prev !== undefined && prev > 0 ? ((value - prev) / prev * 100) : null;
  const isUp = change > 0;
  return (
    <div
      className={`bg-white p-6 border border-[#E8E4DF] flex flex-col justify-between min-h-[130px] ${onClick ? 'cursor-pointer hover:border-[#9A7B4F]/40 hover:shadow-sm transition-all' : ''}`}
      data-testid={testId}
      onClick={onClick}
    >      <div className="flex items-center justify-between mb-3">
        <span className="text-xs tracking-[0.08em] uppercase text-[#676664]/70 font-medium">{label}</span>
        {Icon && <Icon className="w-5 h-5 text-[#9A7B4F]/30" strokeWidth={1.5} />}
      </div>
      <div>
        <div className="text-3xl font-semibold text-[#282828] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
          {prefix}{typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value}{suffix}
        </div>
        {subtitle && <div className="text-[11px] text-[#676664]/50 mt-1">{subtitle}</div>}
        {change !== null && (
          <div className={`flex items-center gap-1 mt-2 text-xs ${isUp ? 'text-[#4A7C59]' : 'text-[#B85C38]'}`}>
            {isUp ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
            {Math.abs(change).toFixed(1)}% vs prev period
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tabs ────────────────────────────
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "products", label: "Products" },
  { id: "revenue", label: "Revenue" },
  { id: "profitability", label: "Profitability" },
  { id: "customers", label: "Customers" },
  { id: "wishlist", label: "Wishlist" },
  { id: "funnel", label: "Funnel" },
  { id: "inventory", label: "Inventory" },
  { id: "operational", label: "Operations" },
];

// ─── Main Component ────────────────────────────
export default function AdminAnalytics() {
  const navigate = useNavigate();
  const location = useLocation();
  // P-4 — honour `?tab=<id>` on first mount so cross-links from the Costs
  // page and any bookmarked deep-links land on the correct tab. Bad values
  // fall back to "overview".
  const initialTab = (() => {
    const t = new URLSearchParams(location.search).get("tab");
    const allowed = ["overview","products","revenue","customers","wishlist","funnel","inventory","operational","profitability"];
    return allowed.includes(t) ? t : "overview";
  })();
  const [tab, setTab] = useState(initialTab);
  const [period, setPeriod] = useState("month");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const loadTab = useCallback(async (t, p) => {
    setLoading(true);
    try {
      let result;
      switch (t) {
        case "overview": {
          const [overview, trend] = await Promise.all([
            fetchApi("/overview", `?period=${p}`),
            fetchApi("/revenue-trend", "?months=6"),
          ]);
          result = { overview, trend };
          break;
        }
        case "products":
          result = { products: await fetchApi("/product-performance") };
          break;
        case "revenue":
          result = { breakdown: await fetchApi("/revenue-breakdown") };
          break;
        case "customers":
          result = { customers: await fetchApi("/customers") };
          break;
        case "wishlist":
          result = { wishlist: await fetchApi("/wishlist") };
          break;
        case "funnel":
          result = { funnel: await fetchApi(`/funnel`, `?period=${p}`) };
          break;
        case "inventory":
          result = { inventory: await fetchApi("/inventory") };
          break;
        case "operational":
          result = { ops: await fetchApi("/operational") };
          break;
        case "profitability": {
          const [summary, trend, byProduct, byCat, byBooking] = await Promise.all([
            fetchProfit(`/summary`, `?period=${p}`),
            fetchProfit(`/trend`, `?granularity=month&months=6`),
            fetchProfit(`/by-product`, `?period=${p}`),
            fetchProfit(`/by-cost-category`, `?period=${p}`),
            fetchProfit(`/by-booking`, `?period=${p}`),
          ]);
          result = { summary, trend, byProduct, byCat, byBooking };
          break;
        }
        default:
          result = {};
      }
      setData(prev => ({ ...prev, [t]: result }));
    } catch (err) {
      if (err.message === "unauthorized") navigate("/admin/login");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadTab(tab, period); }, [tab, period, loadTab]);

  const renderContent = () => {
    if (loading) return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" />
      </div>
    );
    const d = data[tab];
    if (!d) return null;
    switch (tab) {
      case "overview": return <OverviewTab data={d} period={period} />;
      case "products": return <ProductsTab data={d} />;
      case "revenue": return <RevenueTab data={d} />;
      case "customers": return <CustomersTab data={d} />;
      case "wishlist": return <WishlistTab data={d} />;
      case "funnel": return <FunnelTab data={d} />;
      case "inventory": return <InventoryTab data={d} />;
      case "operational": return <OperationalTab data={d} />;
      case "profitability": return <ProfitabilityTab data={d} period={period} />;
      default: return null;
    }
  };

  return (
    <AdminLayout title="Analytics" subtitle="Platform performance & insights">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div className="flex gap-1 overflow-x-auto pb-1" data-testid="analytics-tabs">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-xs tracking-[0.06em] uppercase whitespace-nowrap transition-colors ${
                tab === t.id ? 'bg-[#6B5B4F] text-white' : 'text-[#676664]/60 hover:text-[#282828] hover:bg-[#F0E8DC]'
              }`}
              data-testid={`tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {["overview", "funnel", "profitability"].includes(tab) && (
          <div className="flex gap-1" data-testid="period-selector">
            {[
              { v: "today", l: "Today" }, { v: "week", l: "7d" },
              { v: "month", l: "30d" }, { v: "3months", l: "90d" },
              { v: "6months", l: "6m" }, { v: "year", l: "1y" },
            ].map(p => (
              <button
                key={p.v}
                onClick={() => setPeriod(p.v)}
                className={`px-3 py-1.5 text-[11px] tracking-[0.06em] uppercase transition-colors ${
                  period === p.v ? 'bg-[#9A7B4F] text-white' : 'text-[#676664]/50 hover:text-[#282828]'
                }`}
                data-testid={`period-${p.v}`}
              >
                {p.l}
              </button>
            ))}
          </div>
        )}
      </div>
      {renderContent()}
    </AdminLayout>
  );
}

// ═══════════════════════════════════════
// TAB: Overview
// ═══════════════════════════════════════
function OverviewTab({ data: { overview: o, trend }, period }) {
  const [drilldown, setDrilldown] = useState(null); // { title, type }
  const [emailTarget, setEmailTarget] = useState(null); // { email, name } or { recipients: [...] }

  const openDrill = (title, type) => setDrilldown({ title, type });
  const handleComposeEmail = (email, name) => {
    setEmailTarget({ email, name });
  };
  const handleComposeBulkEmail = (recipients) => {
    setEmailTarget({ recipients });
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="kpi-cards">
        <KpiCard label="Revenue" value={o.revenue} prev={o.prev_revenue} prefix="$" icon={DollarSign} testId="kpi-revenue" onClick={() => openDrill("Revenue — Paid Bookings", "revenue")} />
        <KpiCard label="Bookings" value={o.total_bookings} prev={o.prev_bookings} icon={ShoppingBag} subtitle={o.products_rented != null ? `${o.products_rented} products rented` : undefined} testId="kpi-bookings" onClick={() => openDrill("All Bookings", "bookings")} />
        <KpiCard label="Avg. Booking Value" value={o.avg_booking_value} prefix="$" icon={TrendingUp} testId="kpi-avg-value" onClick={() => openDrill("Bookings — Avg Value", "avg_value")} />
        <KpiCard label="Conversion Rate" value={o.conversion_rate} suffix="%" icon={BarChart3} subtitle="Product views to bookings" testId="kpi-conversion" onClick={() => openDrill("Conversion — Product Views", "conversion")} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="realtime-widgets">
        <KpiCard label="Active Rentals" value={o.active_rentals} icon={Package} subtitle="Currently with customers" testId="kpi-active" onClick={() => openDrill("Active Rentals", "active_rentals")} />
        <KpiCard label="Pending Returns" value={o.pending_returns} icon={Clock} subtitle="Past return date" testId="kpi-pending" onClick={() => openDrill("Pending Returns", "pending_returns")} />
        <KpiCard label="Product Views" value={o.product_views} icon={Eye} subtitle="This period" testId="kpi-views" onClick={() => openDrill("Product Views", "product_views")} />
        <KpiCard label="Repeat Rate" value={o.repeat_rate} suffix="%" icon={TrendingUp} subtitle="Multi-booking customers" testId="kpi-repeat-rate" onClick={() => openDrill("Repeat Customers", "repeat")} />
      </div>

      {trend.length > 0 && (
        <div className="bg-white border border-[#E8E4DF] p-6" data-testid="revenue-trend-chart">
          <h3 className="text-sm font-medium text-[#282828] mb-1">Revenue Trend</h3>
          <p className="text-xs text-[#676664]/50 mb-5">Last 6 months</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DF" />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#676664' }} />
              <YAxis tick={{ fontSize: 12, fill: '#676664' }} />
              <Tooltip contentStyle={{ fontSize: 13, border: '1px solid #E8E4DF' }} />
              <Bar dataKey="revenue" fill="#9A7B4F" radius={[4, 4, 0, 0]} name="Revenue ($)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard label="Paying Customers" value={o.paying_customers || 0} icon={UserCheck} subtitle="With successful bookings" testId="kpi-paying" onClick={() => openDrill("Paying Customers", "paying")} />
        <KpiCard label="New Customers" value={o.new_customers} icon={UserPlus} subtitle="Joined this period" testId="kpi-new-customers" onClick={() => openDrill("New Customers (30 days)", "new_customers")} />
        <KpiCard label="Registered Accounts" value={o.total_customers} icon={Users} subtitle="All sign-ups" testId="kpi-total-accounts" onClick={() => openDrill("All Registered Accounts", "registered")} />
        <KpiCard label="Repeat Customers" value={o.repeat_customers} icon={TrendingUp} subtitle="2+ bookings" testId="kpi-repeat" onClick={() => openDrill("Repeat Customers", "repeat")} />
      </div>

      {/* Drilldown Panel */}
      {drilldown && (
        <DrilldownPanel
          title={drilldown.title}
          type={drilldown.type}
          period={period}
          onClose={() => setDrilldown(null)}
          onComposeEmail={handleComposeEmail}
          onComposeBulkEmail={handleComposeBulkEmail}
        />
      )}

      {/* Email Composer */}
      {emailTarget && (
        <EmailComposer
          toEmail={emailTarget.email}
          toName={emailTarget.name}
          recipients={emailTarget.recipients}
          onClose={() => setEmailTarget(null)}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════
// TAB: Products
// ═══════════════════════════════════════
function ProductsTab({ data: { products } }) {
  const [sort, setSort] = useState("revenue");
  const sorted = [...products].sort((a, b) => b[sort] - a[sort]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs text-[#676664]/60">
        <span className="font-medium">Sort by:</span>
        {["revenue", "bookings", "views", "wishlist_saves", "conversion_rate"].map(s => (
          <button key={s} onClick={() => setSort(s)}
            className={`px-3 py-1 transition-colors ${sort === s ? 'bg-[#6B5B4F] text-white' : 'hover:bg-[#F0E8DC]'}`}
          >{s.replace(/_/g, ' ')}</button>
        ))}
      </div>

      <div className="bg-white border border-[#E8E4DF] overflow-x-auto" data-testid="product-performance-table">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-[#E8E4DF] text-[11px] uppercase tracking-wider text-[#676664]/60 font-medium">
              <th className="p-4">Product</th>
              <th className="p-4 text-right">Views</th>
              <th className="p-4 text-right">Saves</th>
              <th className="p-4 text-right">Cart</th>
              <th className="p-4 text-right">Bookings</th>
              <th className="p-4 text-right">Conv. %</th>
              <th className="p-4 text-right">Revenue</th>
              <th className="p-4 text-right">Rev/mo</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(p => (
              <tr key={p.outfit_id} className="border-b border-[#E8E4DF]/50 hover:bg-[#F5F3F0] text-[13px]">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {p.image && <img src={p.image.startsWith('/') ? `${API}${p.image}` : p.image} alt="" className="w-9 h-11 object-cover flex-shrink-0" />}
                    <div>
                      <div className="font-medium text-[#282828] truncate max-w-[180px]">{p.name}</div>
                      <div className="text-[11px] text-[#676664]/40 mt-0.5">{p.category}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-right text-[#676664]">{p.views}</td>
                <td className="p-4 text-right text-[#676664]">{p.wishlist_saves}</td>
                <td className="p-4 text-right text-[#676664]">{p.add_to_cart}</td>
                <td className="p-4 text-right text-[#282828] font-medium">{p.bookings}</td>
                <td className="p-4 text-right">
                  <span className={p.conversion_rate > 5 ? 'text-[#4A7C59] font-medium' : 'text-[#676664]'}>{p.conversion_rate}%</span>
                </td>
                <td className="p-4 text-right text-[#282828] font-medium">${p.revenue}</td>
                <td className="p-4 text-right text-[#676664]">${p.revenue_per_month}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {products.length === 0 && <EmptyState text="No product data yet" />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// TAB: Revenue
// ═══════════════════════════════════════
function RevenueTab({ data: { breakdown } }) {
  const { by_category, by_delivery, by_duration } = breakdown;
  return (
    <div className="space-y-8">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E8E4DF] p-6" data-testid="revenue-by-category">
          <h3 className="text-sm font-medium text-[#282828] mb-1">Revenue by Category</h3>
          <p className="text-xs text-[#676664]/50 mb-4">Breakdown of earnings per category</p>
          {by_category.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={by_category} dataKey="revenue" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {by_category.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No booking data yet" />}
        </div>

        <div className="bg-white border border-[#E8E4DF] p-6" data-testid="revenue-by-duration">
          <h3 className="text-sm font-medium text-[#282828] mb-1">Revenue by Rental Duration</h3>
          <p className="text-xs text-[#676664]/50 mb-4">4-day vs 8-day bookings</p>
          {by_duration.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={by_duration}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DF" />
                <XAxis dataKey="days" tick={{ fontSize: 12 }} tickFormatter={v => `${v} days`} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                <Bar dataKey="revenue" fill="#9A7B4F" radius={[4, 4, 0, 0]} name="Revenue" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState text="No booking data yet" />}
        </div>
      </div>

      <div className="bg-white border border-[#E8E4DF] p-6" data-testid="delivery-breakdown">
        <h3 className="text-sm font-medium text-[#282828] mb-1">Delivery vs Pickup</h3>
        <p className="text-xs text-[#676664]/50 mb-4">How customers receive their orders</p>
        {by_delivery.length > 0 ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {by_delivery.map(d => (
              <div key={d.method} className="text-center p-5 bg-[#F5F3F0]">
                <div className="text-2xl font-semibold text-[#282828]">{d.bookings}</div>
                <div className="text-[11px] uppercase tracking-wider text-[#676664]/60 font-medium mt-1">{d.method}</div>
                <div className="text-sm text-[#9A7B4F] font-medium mt-1">${d.revenue}</div>
              </div>
            ))}
          </div>
        ) : <EmptyState text="No booking data yet" />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// TAB: Customers
// ═══════════════════════════════════════
function CustomersTab({ data: { customers: c } }) {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard label="Paying Customers" value={c.paying_customers || 0} icon={UserCheck} subtitle="With successful bookings" testId="kpi-cust-paying" />
        <KpiCard label="Registered Accounts" value={c.total_customers} icon={Users} subtitle="All sign-ups" testId="kpi-cust-total" />
        <KpiCard label="Avg. Lifetime Value" value={c.avg_lifetime_value} prefix="$" icon={DollarSign} subtitle="Per paying customer" testId="kpi-cust-ltv" />
        <KpiCard label="Avg. Bookings" value={c.avg_bookings_per_customer} icon={ShoppingBag} subtitle="Per paying customer" testId="kpi-cust-avg" />
      </div>

      <div className="bg-white border border-[#E8E4DF]" data-testid="top-customers-table">
        <div className="p-5 border-b border-[#E8E4DF]">
          <h3 className="text-sm font-medium text-[#282828]">Top Customers by Spend</h3>
          <p className="text-xs text-[#676664]/50 mt-0.5">Only customers with successful bookings</p>
        </div>
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-[#E8E4DF] text-[11px] uppercase tracking-wider text-[#676664]/60 font-medium">
              <th className="p-4">Email</th>
              <th className="p-4 text-right">Bookings</th>
              <th className="p-4 text-right">Total Spent</th>
              <th className="p-4 text-right">First Booking</th>
              <th className="p-4 text-right">Last Booking</th>
            </tr>
          </thead>
          <tbody>
            {c.top_customers.map(cust => (
              <tr key={cust.email} className="border-b border-[#E8E4DF]/50 hover:bg-[#F5F3F0] text-[13px]">
                <td className="p-4 text-[#282828]">{cust.email}</td>
                <td className="p-4 text-right text-[#676664]">{cust.bookings}</td>
                <td className="p-4 text-right text-[#282828] font-medium">${cust.total_spent}</td>
                <td className="p-4 text-right text-[#676664] text-xs">{cust.first_booking?.slice(0, 10)}</td>
                <td className="p-4 text-right text-[#676664] text-xs">{cust.last_booking?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {c.top_customers.length === 0 && <EmptyState text="No customers with bookings yet. Customers will appear here once they complete a booking." />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// TAB: Wishlist
// ═══════════════════════════════════════
function WishlistTab({ data: { wishlist: w } }) {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
        <KpiCard label="Total Wishlist Saves" value={w.total_saves} icon={Heart} testId="kpi-wishlist-total" />
        <KpiCard label="Products Wishlisted" value={w.top_wishlisted.length} icon={Package} testId="kpi-wishlist-products" />
        <KpiCard label="Avg. Saves per Product" value={w.top_wishlisted.length > 0 ? Math.round(w.total_saves / w.top_wishlisted.length * 10) / 10 : 0} icon={TrendingUp} testId="kpi-wishlist-avg" />
      </div>

      {w.trend.length > 0 && (
        <div className="bg-white border border-[#E8E4DF] p-6" data-testid="wishlist-trend">
          <h3 className="text-sm font-medium text-[#282828] mb-1">Wishlist Activity Over Time</h3>
          <p className="text-xs text-[#676664]/50 mb-4">Adds and removes per day</p>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={w.trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E8E4DF" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="adds" stroke="#9A7B4F" strokeWidth={2} dot={false} name="Adds" />
              <Line type="monotone" dataKey="removes" stroke="#B85C38" strokeWidth={1} dot={false} name="Removes" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="bg-white border border-[#E8E4DF]" data-testid="top-wishlisted-table">
        <div className="p-5 border-b border-[#E8E4DF]">
          <h3 className="text-sm font-medium text-[#282828]">Most Wishlisted Products</h3>
          <p className="text-xs text-[#676664]/50 mt-0.5">Ranked by number of saves</p>
        </div>
        <div className="divide-y divide-[#E8E4DF]/50">
          {w.top_wishlisted.map(item => (
            <div key={item.outfit_id} className="flex items-center gap-4 p-5 hover:bg-[#F5F3F0]">
              {item.image && <img src={item.image.startsWith('/') ? `${API}${item.image}` : item.image} alt="" className="w-11 h-14 object-cover flex-shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-[#282828] truncate">{item.name}</div>
                <div className="text-xs text-[#676664]/50 mt-0.5">{item.saves} saves &middot; {item.bookings} bookings</div>
              </div>
              <div className="text-right">
                <div className={`text-[13px] font-medium ${item.conversion > 0 ? 'text-[#4A7C59]' : 'text-[#676664]/40'}`}>
                  {item.conversion}% conversion
                </div>
              </div>
            </div>
          ))}
          {w.top_wishlisted.length === 0 && <EmptyState text="No wishlist data yet" />}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// TAB: Funnel
// ═══════════════════════════════════════
function FunnelTab({ data: { funnel: f } }) {
  const { funnel, cart_abandonment_rate } = f;
  const stageLabels = {
    product_view: "Product Views",
    wishlist_add: "Wishlist Adds",
    add_to_cart: "Add to Cart",
    checkout_started: "Checkout Started",
    booking_completed: "Booking Completed",
  };

  const hasData = funnel.some(s => s.count > 0);

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <KpiCard label="Cart Abandonment" value={cart_abandonment_rate} suffix="%" icon={AlertTriangle} subtitle="Carts not reaching checkout" testId="kpi-cart-abandon" />
      </div>

      <div className="bg-white border border-[#E8E4DF] p-6" data-testid="funnel-chart">
        <h3 className="text-sm font-medium text-[#282828] mb-1">Booking Funnel</h3>
        <p className="text-xs text-[#676664]/50 mb-6">Where users drop off in the booking journey</p>
        {hasData ? (
          <div className="space-y-4">
            {funnel.map((stage, i) => {
              const maxCount = Math.max(...funnel.map(s => s.count), 1);
              const width = Math.max(5, (stage.count / maxCount) * 100);
              return (
                <div key={stage.stage} className="flex items-center gap-4">
                  <div className="w-40 text-xs text-[#676664] text-right font-medium">{stageLabels[stage.stage] || stage.stage}</div>
                  <div className="flex-1 relative">
                    <div className="h-9 bg-[#F5F3F0] rounded">
                      <div
                        className="h-full rounded transition-all flex items-center pl-3"
                        style={{ width: `${width}%`, backgroundColor: COLORS[i % COLORS.length] }}
                      >
                        <span className="text-white text-xs font-medium">{stage.count}</span>
                      </div>
                    </div>
                  </div>
                  <div className="w-20 text-right">
                    {i > 0 && stage.drop_off > 0 && (
                      <div className="text-xs text-[#B85C38] font-medium">-{stage.drop_off}% drop</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState text="Funnel data will appear as customers interact with the platform. Events are being tracked automatically." />
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// TAB: Inventory
// ═══════════════════════════════════════
function InventoryTab({ data: { inventory } }) {
  const [sort, setSort] = useState("utilization");
  const sorted = [...inventory].sort((a, b) => b[sort] - a[sort]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs text-[#676664]/60">
        <span className="font-medium">Sort by:</span>
        {["utilization", "revenue", "profit", "bookings", "missed_demand"].map(s => (
          <button key={s} onClick={() => setSort(s)}
            className={`px-3 py-1 transition-colors ${sort === s ? 'bg-[#6B5B4F] text-white' : 'hover:bg-[#F0E8DC]'}`}
          >{s.replace(/_/g, ' ')}</button>
        ))}
      </div>

      <div className="bg-white border border-[#E8E4DF] overflow-x-auto" data-testid="inventory-table">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-[#E8E4DF] text-[11px] uppercase tracking-wider text-[#676664]/60 font-medium">
              <th className="p-4">Product</th>
              <th className="p-4 text-right">Utilization</th>
              <th className="p-4 text-right">Bookings</th>
              <th className="p-4 text-right">Revenue</th>
              <th className="p-4 text-right">Repairs</th>
              <th className="p-4 text-right">Profit</th>
              <th className="p-4 text-right">Missed</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(item => (
              <tr key={item.outfit_id} className="border-b border-[#E8E4DF]/50 hover:bg-[#F5F3F0] text-[13px]">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {item.image && <img src={item.image.startsWith('/') ? `${API}${item.image}` : item.image} alt="" className="w-9 h-11 object-cover flex-shrink-0" />}
                    <div>
                      <div className="font-medium text-[#282828] truncate max-w-[160px]">{item.name}</div>
                      <div className="text-[11px] text-[#676664]/40">{item.category}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-2 bg-[#E8E4DF] rounded overflow-hidden">
                      <div className="h-full rounded" style={{ width: `${Math.min(item.utilization, 100)}%`, backgroundColor: item.utilization > 50 ? '#4A7C59' : item.utilization > 20 ? '#9A7B4F' : '#B85C38' }} />
                    </div>
                    <span className="text-xs text-[#676664]">{item.utilization}%</span>
                  </div>
                </td>
                <td className="p-4 text-right text-[#676664]">{item.bookings}</td>
                <td className="p-4 text-right text-[#282828] font-medium">${item.revenue}</td>
                <td className="p-4 text-right text-[#676664]">{item.repairs} (${item.repair_cost})</td>
                <td className="p-4 text-right font-medium" style={{ color: item.profit >= 0 ? '#4A7C59' : '#B85C38' }}>${item.profit}</td>
                <td className="p-4 text-right">
                  {item.missed_demand > 0 ? (
                    <span className="text-[#B85C38] font-medium">{item.missed_demand}</span>
                  ) : (
                    <span className="text-[#676664]/30">0</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {inventory.length === 0 && <EmptyState text="No inventory data yet" />}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════
// TAB: Operational
// ═══════════════════════════════════════
function OperationalTab({ data: { ops } }) {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="ops-kpis">
        <KpiCard label="Completed Bookings" value={ops.completed_bookings} icon={ShoppingBag} testId="kpi-ops-completed" />
        <KpiCard label="Late Returns" value={ops.late_returns} icon={Clock} testId="kpi-ops-late" />
        <KpiCard label="Late Return Rate" value={ops.late_return_rate} suffix="%" icon={AlertTriangle} testId="kpi-ops-late-rate" />
        <KpiCard label="Due Today" value={ops.items_due_today} icon={Package} subtitle="Items to be returned" testId="kpi-ops-due" />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E8E4DF] p-6 min-h-[130px]" data-testid="late-fees-card">
          <h3 className="text-sm font-medium text-[#282828] mb-1">Late Fees Collected</h3>
          <p className="text-xs text-[#676664]/50 mb-3">{ops.late_returns} late returns</p>
          <div className="text-3xl font-semibold text-[#282828]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            ${ops.total_late_fees}
          </div>
        </div>

        <div className="bg-white border border-[#E8E4DF] p-6 min-h-[130px]" data-testid="damage-costs-card">
          <h3 className="text-sm font-medium text-[#282828] mb-1">Damage & Repair Costs</h3>
          <p className="text-xs text-[#676664]/50 mb-3">{ops.damaged_items} damaged items</p>
          <div className="text-3xl font-semibold text-[#282828]" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            ${ops.total_damage_costs}
          </div>
        </div>
      </div>
      {ops.extra_charges && <ExtraChargesCards extra={ops.extra_charges} />}
    </div>
  );
}

const EXTRA_METHOD_LABELS = [
  ["stripe_saved_card", "Saved card charged"],
  ["stripe_link", "Stripe payment link"],
  ["bond_deduction", "Deducted from bond"],
  ["external", "Paid externally"],
];

function ExtraChargesCards({ extra }) {
  const m = (n) => `$${(Number(n) || 0).toFixed(2)}`;
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="bg-white border border-[#E8E4DF] p-6" data-testid="extra-charges-by-method-card">
        <h3 className="text-sm font-medium text-[#282828] mb-1">Extra Charges Collected</h3>
        <p className="text-xs text-[#676664]/50 mb-4">{extra.paid_count} paid charges · {m(extra.paid_total)}</p>
        <div className="space-y-2">
          {EXTRA_METHOD_LABELS.map(([k, label]) => (
            <div key={k} className="flex justify-between text-[13px] border-b border-[#282828]/5 pb-1.5" data-testid={`extra-method-${k}`}>
              <span className="text-[#676664]/70">{label} ({extra.paid_by_method?.[k]?.count || 0})</span>
              <span className="text-[#282828]">{m(extra.paid_by_method?.[k]?.total)}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-white border border-[#E8E4DF] p-6" data-testid="extra-charges-outstanding-card">
        <h3 className="text-sm font-medium text-[#282828] mb-1">Outstanding Charges</h3>
        <p className="text-xs text-[#676664]/50 mb-3">{extra.outstanding_count} unpaid charges</p>
        <div className="text-3xl font-semibold text-[#B8761C]" style={{ fontFamily: "'Montserrat', sans-serif" }} data-testid="extra-outstanding-total">{m(extra.outstanding_total)}</div>
        <div className="text-[12px] text-[#676664]/70 mt-3 space-y-1">
          <div>Collect externally: {m(extra.outstanding_external_total)}</div>
          <div>Stripe links unpaid: {m(extra.outstanding_link_total)}</div>
          {extra.needs_review_count > 0 && <div className="text-orange-700" data-testid="extra-needs-review">{extra.needs_review_count} historical charge(s) need review · {m(extra.needs_review_total)}</div>}
        </div>
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="flex items-center justify-center py-12 text-[#676664]/40 text-sm">
      {text}
    </div>
  );
}

// ═══════════════════════════════════════
// TAB: Profitability (Phase 4 + 5)
// ═══════════════════════════════════════
function ProfitabilityTab({ data, period }) {
  const s = data.summary || {};
  const trend = data.trend?.points || [];
  const byProduct = data.byProduct?.items || [];
  const byCat = data.byCat?.items || [];
  const byBooking = data.byBooking?.items || [];

  const exportFile = async (kind, format) => {
    const url = `${API}/api/admin/profitability/export/${kind}?period=${period}&format=${format}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
    if (!res.ok) return;
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${kind}-${new Date().toISOString().slice(0, 10)}.${format}`;
    a.click();
  };

  const maxAbsNet = Math.max(1, ...trend.map(p => Math.abs(p.net_profit || 0)));
  const catTotal = byCat.reduce((sum, c) => sum + c.amount, 0) || 1;

  return (
    <div className="space-y-8" data-testid="profitability-tab">
      {/* P-4 — cross-link back to the operational Costs ledger. This
          reporting tab summarises numbers; CRUD lives on /admin/costs. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-[12px] text-[#676664]/70 max-w-2xl leading-relaxed">
          Reporting view of your operating costs. All entries and CRUD live in the Costs ledger — this tab summarises the numbers and rolls them into Net Profit.
        </p>
        <Link
          to="/admin/costs"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#9A7B4F]/40 text-[11px] text-[#9A7B4F] hover:bg-[#9A7B4F]/5 whitespace-nowrap self-start sm:self-auto"
          data-testid="profitability-manage-costs-btn"
        >
          <Receipt className="w-3.5 h-3.5" strokeWidth={1.5} /> Manage cost entries →
        </Link>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="profit-kpis">
        <KpiCard label="Gross Revenue" value={s.gross_revenue || 0} prefix="$" icon={DollarSign} subtitle={`${s.kept_booking_count || 0} kept · ${s.paid_charge_count || 0} charges`} testId="kpi-prof-gross" />
        <KpiCard label="Stripe Fees" value={s.stripe_fees || 0} prefix="-$" icon={TrendingUp} subtitle={`${s.stripe_fee_unknown_count || 0} not captured`} testId="kpi-prof-fees" />
        <KpiCard label="Business Costs" value={s.business_costs || 0} prefix="-$" icon={TrendingUp} subtitle="Operating (excl. procurement)" testId="kpi-prof-costs" />
        <KpiCard label="Net Profit" value={s.net_profit || 0} prefix="$" suffix={` (${(s.profit_margin_pct ?? 0).toFixed(1)}%)`} icon={TrendingUp} testId="kpi-prof-net" />
      </div>
      {s.charges_by_method && s.paid_charge_count > 0 && (
        <div className="bg-white border border-[#E8E4DF] px-6 py-4 flex flex-wrap gap-x-8 gap-y-2 text-[12px]" data-testid="profit-charges-by-method">
          <span className="text-[#676664]/60 uppercase tracking-wider text-[10px] self-center">Extra charges in revenue</span>
          {EXTRA_METHOD_LABELS.map(([k, label]) => (
            <span key={k} className="text-[#676664]" data-testid={`profit-method-${k}`}>{label}: <strong className="text-[#282828]">${(Number(s.charges_by_method[k]?.total) || 0).toFixed(2)}</strong></span>
          ))}
        </div>
      )}

      {/* Lifetime Inventory Recovery — NOT a period expense */}
      <LifetimeInventoryRecoveryPanel />


      {/* Trend chart */}
      <div className="bg-white p-6 border border-[#E8E4DF]" data-testid="profit-trend">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm tracking-[0.08em] uppercase text-[#676664]/80">Net Profit — Last 6 Months</h3>
          <div className="flex gap-2">
            <button onClick={() => exportFile("summary", "csv")} className="text-[10px] tracking-wider uppercase text-[#9A7B4F] hover:underline" data-testid="export-summary-csv">Export Summary CSV</button>
            <span className="text-[10px] text-[#676664]/30">·</span>
            <button onClick={() => exportFile("summary", "xlsx")} className="text-[10px] tracking-wider uppercase text-[#9A7B4F] hover:underline" data-testid="export-summary-xlsx">XLSX</button>
          </div>
        </div>
        {trend.length === 0 ? <EmptyState text="No trend data yet." /> : (
          <div className="flex items-end gap-3 h-40 px-2">
            {trend.map((p) => {
              const heightPct = Math.max(2, (Math.abs(p.net_profit) / maxAbsNet) * 100);
              const positive = p.net_profit >= 0;
              return (
                <div key={p.label} className="flex-1 flex flex-col items-center gap-2">
                  <div className="text-[10px] text-[#676664]/60 font-medium">${p.net_profit.toFixed(0)}</div>
                  <div
                    className={`w-full ${positive ? "bg-[#0E7C5A]" : "bg-[#B23A48]"}`}
                    style={{ height: `${heightPct}%`, minHeight: "4px" }}
                    title={`${p.label}: $${p.net_profit.toFixed(2)} (${p.profit_margin_pct.toFixed(1)}%)`}
                  />
                  <div className="text-[10px] text-[#676664]/70 tracking-wider">{p.label}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Cost donut + by-product */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white p-6 border border-[#E8E4DF]" data-testid="profit-cost-breakdown">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm tracking-[0.08em] uppercase text-[#676664]/80">Cost Breakdown</h3>
            <div className="flex gap-2">
              <button onClick={() => exportFile("costs", "csv")} className="text-[10px] tracking-wider uppercase text-[#9A7B4F] hover:underline" data-testid="export-costs-csv">CSV</button>
              <span className="text-[10px] text-[#676664]/30">·</span>
              <button onClick={() => exportFile("costs", "xlsx")} className="text-[10px] tracking-wider uppercase text-[#9A7B4F] hover:underline" data-testid="export-costs-xlsx">XLSX</button>
            </div>
          </div>
          {byCat.length === 0 ? <EmptyState text="No costs in this period." /> : (
            <div className="space-y-3">
              {byCat.map((c) => {
                const pct = (c.amount / catTotal * 100);
                return (
                  <div key={c.category}>
                    <div className="flex justify-between text-[12px] mb-1">
                      <span className="text-[#282828]">{c.category_label}</span>
                      <span className="text-[#676664]">${c.amount.toFixed(2)} <span className="text-[#676664]/40">({pct.toFixed(0)}%)</span></span>
                    </div>
                    <div className="h-2 bg-[#FAF8F0]"><div className="h-2 bg-[#9A7B4F]" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white p-6 border border-[#E8E4DF]" data-testid="profit-by-product">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm tracking-[0.08em] uppercase text-[#676664]/80">Profit by Product</h3>
            <div className="flex gap-2">
              <button onClick={() => exportFile("by-product", "csv")} className="text-[10px] tracking-wider uppercase text-[#9A7B4F] hover:underline" data-testid="export-product-csv">CSV</button>
              <span className="text-[10px] text-[#676664]/30">·</span>
              <button onClick={() => exportFile("by-product", "xlsx")} className="text-[10px] tracking-wider uppercase text-[#9A7B4F] hover:underline" data-testid="export-product-xlsx">XLSX</button>
            </div>
          </div>
          {byProduct.length === 0 ? <EmptyState text="No bookings in this period." /> : (
            <table className="w-full text-[12px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[#676664]/60 border-b border-[#282828]/5">
                  <th className="text-left py-2">Outfit</th>
                  <th className="text-right py-2">Period Net</th>
                  <th className="text-right py-2">Period %</th>
                  <th className="text-right py-2" title="Lifetime — not a period expense">Recovery %</th>
                </tr>
              </thead>
              <tbody>
                {byProduct.slice(0, 8).map((p) => (
                  <tr key={p.outfit_id} className="border-b border-[#282828]/5">
                    <td className="py-2 text-[#282828]">{p.outfit_name}</td>
                    <td className={`py-2 text-right ${p.net_profit >= 0 ? "text-[#0E7C5A]" : "text-[#B23A48]"}`}>${p.net_profit.toFixed(2)}</td>
                    <td className="py-2 text-right text-[#676664]/70">{p.profit_margin_pct.toFixed(1)}%</td>
                    <td className="py-2 text-right" data-testid={`product-recovery-${p.outfit_id}`}>
                      {p.procurement_cost_entered ? (
                        <span className={p.cost_recovered ? "text-[#0E7C5A]" : "text-[#B23A48]"}>
                          {p.recovery_pct.toFixed(1)}%
                        </span>
                      ) : <span className="text-[#676664]/40">Not entered</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* By booking */}
      <div className="bg-white p-6 border border-[#E8E4DF]" data-testid="profit-by-booking">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm tracking-[0.08em] uppercase text-[#676664]/80">Profit by Booking</h3>
          <div className="flex gap-2">
            <button onClick={() => exportFile("by-booking", "csv")} className="text-[10px] tracking-wider uppercase text-[#9A7B4F] hover:underline" data-testid="export-booking-csv">CSV</button>
            <span className="text-[10px] text-[#676664]/30">·</span>
            <button onClick={() => exportFile("by-booking", "xlsx")} className="text-[10px] tracking-wider uppercase text-[#9A7B4F] hover:underline" data-testid="export-booking-xlsx">XLSX</button>
          </div>
        </div>
        {byBooking.length === 0 ? <EmptyState text="No bookings in this period." /> : (
          <table className="w-full text-[12px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#676664]/60 border-b border-[#282828]/5">
                <th className="text-left py-2">Booking</th>
                <th className="text-left py-2">Customer</th>
                <th className="text-right py-2">Gross</th>
                <th className="text-right py-2">Fees</th>
                <th className="text-right py-2">Costs</th>
                <th className="text-right py-2">Net</th>
                <th className="text-right py-2">Margin</th>
              </tr>
            </thead>
            <tbody>
              {byBooking.slice(0, 15).map((b) => (
                <tr key={b.booking_id} className="border-b border-[#282828]/5">
                  <td className="py-2 font-mono text-[10px] text-[#676664]">{b.booking_id}</td>
                  <td className="py-2 text-[#282828]">{b.customer_name}</td>
                  <td className="py-2 text-right text-[#282828]">${b.gross_revenue.toFixed(2)}</td>
                  <td className="py-2 text-right text-[#B23A48]/70">-${b.stripe_fees.toFixed(2)}</td>
                  <td className="py-2 text-right text-[#B23A48]/70">-${b.business_costs.toFixed(2)}</td>
                  <td className={`py-2 text-right font-medium ${b.net_profit >= 0 ? "text-[#0E7C5A]" : "text-[#B23A48]"}`}>${b.net_profit.toFixed(2)}</td>
                  <td className="py-2 text-right text-[#676664]/70">{b.profit_margin_pct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Section 18.5 Rev 3 — Lifetime Inventory Recovery panel ─────────────
// Reads GET /admin/profitability/lifetime-inventory-recovery. Numbers here are
// stable — they do NOT change with the selected period; procurement is NEVER
// charged against period net profit.
function LifetimeInventoryRecoveryPanel() {
  const [d, setD] = useState(null);
  useEffect(() => {
    fetch(`${API}/api/admin/profitability/lifetime-inventory-recovery`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((r) => r.ok ? r.json() : Promise.reject(r.statusText))
      .then(setD)
      .catch(() => {});
  }, []);
  if (!d) return null;

  const total = d.total_procurement || 0;
  const recovered = d.recovered || 0;
  const unrecovered = d.unrecovered || 0;
  const pct = total > 0 ? Math.round((recovered / total) * 100) : 0;

  return (
    <div className="bg-white border border-[#E8E4DF] p-6" data-testid="lifetime-inventory-recovery">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <h3 className="text-[15px] text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Lifetime Inventory Recovery
          </h3>
          <p className="text-[10px] tracking-widest uppercase text-[#9A7B4F] mt-0.5">
            Lifetime, not a period expense · procurement is never charged against period net
          </p>
        </div>
        <span className="text-[10px] text-[#676664]/60 uppercase tracking-widest">
          {d.outfits_recovered || 0}/{d.outfits_with_cost || 0} outfits recovered
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div>
          <p className="text-[10px] tracking-widest uppercase text-[#676664]/60">Total catalogue procurement</p>
          <p className="text-lg text-[#282828] font-medium mt-1" data-testid="lir-total">${total.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-[10px] tracking-widest uppercase text-[#676664]/60">Lifetime recovered</p>
          <p className="text-lg text-[#0E7C5A] font-medium mt-1" data-testid="lir-recovered">${recovered.toFixed(2)}</p>
        </div>
        <div>
          <p className="text-[10px] tracking-widest uppercase text-[#676664]/60">Lifetime unrecovered</p>
          <p className="text-lg text-[#B23A48] font-medium mt-1" data-testid="lir-unrecovered">${unrecovered.toFixed(2)}</p>
        </div>
      </div>
      {total > 0 && (
        <div className="h-2 bg-[#F0EBE0] overflow-hidden">
          <div className="h-2 bg-[#0E7C5A]" style={{ width: `${Math.min(100, pct)}%` }} data-testid="lir-progress" />
        </div>
      )}
    </div>
  );
}

